import { Alert, Button, Card, Form, Input, Select, Space, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationStudentSelect } from '@/pages/education/components/EducationLookupSelects';

type Template = { id: string; code: string; name: string; sequence_no: number };
type Values = { student_id: string; template_id: string; status: string; notes?: string };

export default function HpcStageFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const permissions = useAuthStore((s) => s.user?.permissions || []);
  const canManage = permissions.includes('education.hpc.manage') || permissions.includes('education.admin');
  const templates = useQuery({
    queryKey: ['education-hpc-templates'],
    queryFn: async () => (await api.get<Template[]>('/education/hpc/templates')).data || [],
  });
  const save = useMutation({
    mutationFn: (values: Values) => api.post('/education/hpc/stages', {
      ...values,
      notes: values.notes?.trim() || null,
    }),
    onSuccess: (response: { data?: { stage?: { id?: string } } }) => {
      message.success('Student stage assigned');
      queryClient.invalidateQueries({ queryKey: ['education-hpc-stages'] });
      const id = response.data?.stage?.id;
      navigate(id ? `/education/hpc/stages/${id}` : '/education/hpc');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || 'Unable to assign student stage'),
  });

  return (
    <EducationPageChrome
      title="Assign HPC student stage"
      description="Create a student progress stage from a company-defined template."
      extra={<Button onClick={() => navigate('/education/hpc')}>Back to HPC</Button>}
    >
      {!canManage ? <Alert type="warning" showIcon message="HPC manage permission is required." /> : null}
      <Card size="small" style={{ maxWidth: 760 }}>
        <Form<Values>
          layout="vertical"
          initialValues={{ status: 'open' }}
          disabled={!canManage}
          onFinish={(values) => save.mutate(values)}
        >
          <Form.Item name="student_id" label="Student" rules={[{ required: true }]}>
            <EducationStudentSelect />
          </Form.Item>
          <Form.Item name="template_id" label="Stage template" rules={[{ required: true }]}>
            <Select
              showSearch optionFilterProp="label" loading={templates.isLoading}
              options={(templates.data || []).map((row) => ({
                value: row.id,
                label: `${row.sequence_no}. ${row.code} — ${row.name}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select options={[
              { value: 'open', label: 'Open' },
              { value: 'in_progress', label: 'In progress' },
              { value: 'completed', label: 'Completed' },
            ]} />
          </Form.Item>
          <Form.Item name="notes" label="Stage notes">
            <Input.TextArea rows={5} maxLength={4000} showCount />
          </Form.Item>
          {canManage ? <Space>
            <Button type="primary" htmlType="submit" loading={save.isPending}>Assign stage</Button>
            <Button onClick={() => navigate('/education/hpc')}>Cancel</Button>
          </Space> : null}
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
